import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Alert,
  Chip, useTheme, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { Storage as StorageIcon, Analytics as AnalyticsIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import apiClient from 'utils/ApiClient';
import { ReportPanel } from '../Reports/ReportPanel';
import {
  ReportDefinition, ReportSuiteDetail, sortReportsByLayout,
} from '../Settings/reportDsl';
import {
  fetchDefaultReportsClickHouseConfigId,
  resolveReportsSuiteId,
  ReportsModuleConfig,
} from '../reportsDefaultServer';

interface ModuleConfig {
  _id: string;
  moduleType: string;
  name: string;
  tenantId?: string;
  siteId?: string;
  defaultReportSuiteId?: string;
  reportSuites?: Array<{ _id: string; name: string; description?: string }>;
}

interface InfralytiqsDashboardProps {
  /** When true, render reporting content directly (no accordion) and load on mount. */
  embedded?: boolean;
}

export const InfralytiqsDashboard: React.FC<InfralytiqsDashboardProps> = ({ embedded = false }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<ModuleConfig | null>(null);
  const [suiteDetail, setSuiteDetail] = useState<ReportSuiteDetail | null>(null);
  const [suiteName, setSuiteName] = useState<string>('');
  const [expanded, setExpanded] = useState(!!embedded);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, defaultConfigId]: [any, string | null] = await Promise.all([
        apiClient.get('/il/settings/module-configs'),
        fetchDefaultReportsClickHouseConfigId(),
      ]);

      const list: ModuleConfig[] = Array.isArray(res?.data)
        ? res.data
        : (res?.data?.moduleConfigurations ?? []);
      const clickhouseConfigs = list.filter((c) => c.moduleType === 'ClickHouse');

      if (clickhouseConfigs.length === 0) {
        setConfig(null);
        setSuiteDetail(null);
        setSuiteName('');
        setHasFetched(true);
        setLoading(false);
        return;
      }

      const targetConfig = (defaultConfigId
        ? clickhouseConfigs.find((c) => c._id === defaultConfigId)
        : null) ?? clickhouseConfigs[0];

      const suites = targetConfig.reportSuites ?? [];
      if (suites.length === 0) {
        setConfig(targetConfig);
        setSuiteDetail(null);
        setSuiteName('');
        setHasFetched(true);
        setLoading(false);
        return;
      }

      const suiteId = resolveReportsSuiteId(targetConfig as ReportsModuleConfig);
      if (!suiteId) {
        setConfig(targetConfig);
        setSuiteDetail(null);
        setSuiteName('');
        setHasFetched(true);
        setLoading(false);
        return;
      }

      const suiteRes: any = await apiClient.get(
        `/il/settings/module-config/${targetConfig._id}/report-suites/${suiteId}`,
      );
      const detail: ReportSuiteDetail | null = suiteRes?.data ?? null;

      setConfig(targetConfig);
      setSuiteDetail(detail);
      setSuiteName(suites.find((s) => s._id === suiteId)?.name ?? '');
      setHasFetched(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to load Infralytiqs dashboard');
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!expanded) return;
    void fetchData();
  }, [expanded, fetchData]);

  const dashboardReports = useMemo(() => {
    if (!suiteDetail) return [];
    return sortReportsByLayout(
      suiteDetail.reports.filter(
        (r) =>
          r.mode !== 'drilldown' &&
          r.mode !== 'toolbar' &&
          r.visualizations?.[0]?.type !== 'realtime' &&
          r.visualizations?.[0]?.type !== 'realtime_bar',
      ),
    );
  }, [suiteDetail]);

  const accordionLeftColor = theme.palette.dashboard?.welcomePanelLeftTextColor
    ?? (theme.palette.mode === 'light' ? 'black' : 'inherit');
  const accordionRightColor = theme.palette.dashboard?.welcomePanelRightTextColor
    ?? (theme.palette.mode === 'dark' ? 'black' : 'inherit');

  const showEmptyState = hasFetched && !loading && !error && (!config || !suiteDetail || dashboardReports.length === 0);

  const body = (
    <>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
          <CircularProgress size={28} />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {showEmptyState && (
        <Typography variant="body2" color="text.secondary">
          No Infralytiqs dashboard reports are configured.
        </Typography>
      )}
      {expanded && !loading && !error && config && suiteDetail && dashboardReports.length > 0 && (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={3} flexWrap="wrap">
            <Chip
              icon={<StorageIcon sx={{
                color: theme.palette.dashboard?.infralytiqsServerChipColor ?? 'inherit',
              }} />}
              label={config.name}
              size="small"
              variant={theme.palette.dashboard?.infralytiqsServerChipBackground ? 'filled' : 'outlined'}
              color={theme.palette.dashboard?.infralytiqsServerChipBackground ? undefined : 'secondary'}
              sx={theme.palette.dashboard?.infralytiqsServerChipBackground ? {
                bgcolor: theme.palette.dashboard.infralytiqsServerChipBackground,
                color: theme.palette.dashboard.infralytiqsServerChipColor,
                fontWeight: 600,
                '& .MuiChip-icon': { color: theme.palette.dashboard.infralytiqsServerChipColor },
              } : undefined}
            />
            {suiteName && (
              <Chip
                label={suiteName}
                size="small"
                variant={theme.palette.dashboard?.infralytiqsSuiteChipBackground ? 'filled' : 'outlined'}
                color={theme.palette.dashboard?.infralytiqsSuiteChipBackground ? undefined : 'secondary'}
                sx={theme.palette.dashboard?.infralytiqsSuiteChipBackground ? {
                  bgcolor: theme.palette.dashboard.infralytiqsSuiteChipBackground,
                  color: theme.palette.dashboard.infralytiqsSuiteChipColor,
                  fontWeight: 600,
                } : undefined}
              />
            )}
          </Box>

          <Box sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(12, minmax(0, 1fr))' },
          }}>
            {dashboardReports.map((report: ReportDefinition) => (
              <Box
                key={report._id}
                sx={{
                  gridColumn: {
                    xs: 'span 1',
                    md: `span ${report.layout?.w ?? 12}`,
                  },
                  minWidth: 0,
                }}
              >
                <ReportPanel
                  report={report}
                  tenantId={config.tenantId || ''}
                  siteId={config.siteId}
                  timezone={suiteDetail.timezone}
                />
              </Box>
            ))}
          </Box>
        </>
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ px: 1, pb: 1 }}>{body}</Box>;
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      sx={{ mb: 0, borderRadius: '12px !important', '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: accordionRightColor }} />}
        sx={{
          px: 3,
          py: 1,
          borderRadius: '12px',
          background: theme.palette.dashboard?.welcomePanelGradient
            ?? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          '& .MuiAccordionSummary-content': { my: 1 },
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <AnalyticsIcon sx={{ color: accordionLeftColor }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: accordionLeftColor }}>
            Infralytiqs Dashboard
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, pb: 3 }}>
        {body}
      </AccordionDetails>
    </Accordion>
  );
};
